import { discoverIssuer } from './discovery';
import { toAuthUser, verifyIdToken } from './id-token';
import {
  buildAuthorizationUrl,
  buildLogoutUrl,
  exchangeCodeForTokens,
  refreshTokens,
} from './oidc';
import { generatePkcePair, randomUrlSafeString } from './pkce';
import type { TokenStore } from './token-store';
import type {
  AuthUser,
  OidcClientConfig,
  OidcDiscovery,
  StoredTokens,
  TokenResponse,
} from './types';

export interface AuthClientOptions {
  config: OidcClientConfig;
  store: TokenStore;
  /** Refresh access tokens this many seconds before they expire. Default 30. */
  refreshSkewSeconds?: number;
  fetch?: typeof globalThis.fetch;
}

export type AuthEvent =
  | { type: 'login'; user: AuthUser }
  | { type: 'logout' }
  | { type: 'tokens-refreshed' };

export type AuthListener = (event: AuthEvent) => void;

/**
 * Orchestrates the OIDC authorization-code-with-PKCE flow:
 *
 *   const auth = new AuthClient({ config, store: new WebStorageTokenStore(sessionStorage) });
 *   if (window.location.search.includes('code=')) {
 *     await auth.handleCallback(window.location.href);
 *   }
 *   if (!(await auth.isAuthenticated())) {
 *     window.location.assign(await auth.startLogin('/dashboard'));
 *   }
 *
 *   const apiClient = createApiClient({
 *     baseUrl,
 *     getToken: () => auth.getAccessToken(),
 *   });
 */
export class AuthClient {
  private discoveryPromise: Promise<OidcDiscovery> | null = null;
  private refreshPromise: Promise<StoredTokens> | null = null;
  private readonly listeners = new Set<AuthListener>();

  constructor(private readonly options: AuthClientOptions) {}

  get config(): OidcClientConfig {
    return this.options.config;
  }

  /**
   * Build the authorization URL the consumer should redirect/open. The
   * matching PKCE state is persisted to the store so handleCallback() can
   * pick it up after the redirect.
   */
  async startLogin(returnTo?: string): Promise<string> {
    const discovery = await this.discovery();
    const pkce = generatePkcePair();
    const state = randomUrlSafeString(43);
    const nonce = randomUrlSafeString(43);
    await this.options.store.setPendingLogin({
      state,
      nonce,
      codeVerifier: pkce.codeVerifier,
      returnTo,
    });
    return buildAuthorizationUrl({
      config: this.options.config,
      discovery,
      state,
      nonce,
      codeChallenge: pkce.codeChallenge,
    });
  }

  /**
   * Parse `code` and `state` out of a callback URL, validate the state
   * matches the persisted pending login, exchange the code for tokens,
   * verify the ID token, and store the result.
   * Returns the `returnTo` value the caller passed to startLogin(), if any.
   */
  async handleCallback(callbackUrl: string): Promise<{ user: AuthUser; returnTo?: string }> {
    const url = new URL(callbackUrl);
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    const error = url.searchParams.get('error');
    if (error) {
      await this.options.store.clearPendingLogin();
      throw new Error(`Authorization failed: ${error}`);
    }
    if (!code || !state) {
      throw new Error('Callback URL missing `code` or `state`');
    }
    const pending = await this.options.store.getPendingLogin();
    if (!pending) {
      throw new Error('No pending login — possible CSRF or expired session');
    }
    if (pending.state !== state) {
      throw new Error('OAuth state mismatch — possible CSRF');
    }

    const discovery = await this.discovery();
    const tokens = await exchangeCodeForTokens({
      config: this.options.config,
      discovery,
      code,
      codeVerifier: pending.codeVerifier,
      fetch: this.options.fetch,
    });

    await this.options.store.setTokens(tokensToStored(tokens));
    await this.options.store.clearPendingLogin();

    if (!tokens.id_token) {
      throw new Error('Token response missing id_token');
    }
    const claims = await verifyIdToken({
      idToken: tokens.id_token,
      discovery,
      audience: this.options.config.clientId,
      expectedNonce: pending.nonce,
    });

    const user = toAuthUser(claims);
    this.emit({ type: 'login', user });
    return { user, returnTo: pending.returnTo };
  }

  /** Returns a valid access token, refreshing if expired. Null if logged out. */
  async getAccessToken(): Promise<string | null> {
    const tokens = await this.options.store.getTokens();
    if (!tokens) return null;
    if (!this.isExpired(tokens)) return tokens.accessToken;
    if (!tokens.refreshToken) {
      await this.options.store.clearTokens();
      this.emit({ type: 'logout' });
      return null;
    }
    const refreshed = await this.refresh();
    return refreshed?.accessToken ?? null;
  }

  async isAuthenticated(): Promise<boolean> {
    return (await this.getAccessToken()) !== null;
  }

  /**
   * Decoded claims from the stored ID token, refreshing transparently if it
   * has expired. Returns `null` when there is nothing to authenticate as
   * (logged out, no tokens, or refresh failed). Only non-expiry verification
   * errors (signature, issuer, audience, nonce) propagate to the caller.
   */
  async getUser(): Promise<AuthUser | null> {
    const tokens = await this.options.store.getTokens();
    if (!tokens?.idToken) return null;
    const discovery = await this.discovery();
    try {
      const claims = await verifyIdToken({
        idToken: tokens.idToken,
        discovery,
        audience: this.options.config.clientId,
      });
      return toAuthUser(claims);
    } catch (err) {
      if (!isIdTokenExpiredError(err)) throw err;
      if (!tokens.refreshToken) {
        await this.options.store.clearTokens();
        this.emit({ type: 'logout' });
        return null;
      }
      let refreshed: StoredTokens | null;
      try {
        refreshed = await this.refresh();
      } catch {
        return null;
      }
      if (!refreshed?.idToken) return null;
      const claims = await verifyIdToken({
        idToken: refreshed.idToken,
        discovery,
        audience: this.options.config.clientId,
      });
      return toAuthUser(claims);
    }
  }

  /**
   * Clear local tokens and (if the issuer supports RP-Initiated Logout
   * and is reachable) return the URL the consumer should redirect to
   * to also end the Keycloak SSO session. Local token clearing always
   * succeeds even if discovery fails — we don't want a flaky issuer to
   * leave the user "still signed in" on the device.
   */
  async logout(): Promise<string | null> {
    const tokens = await this.options.store.getTokens();
    await this.options.store.clearTokens();
    await this.options.store.clearPendingLogin();
    this.emit({ type: 'logout' });
    try {
      const discovery = await this.discovery();
      return buildLogoutUrl({
        config: this.options.config,
        discovery,
        idToken: tokens?.idToken,
      });
    } catch {
      // Discovery unreachable — local logout already happened, that's enough.
      this.discoveryPromise = null;
      return null;
    }
  }

  onChange(listener: AuthListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private async refresh(): Promise<StoredTokens | null> {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = this.doRefresh().finally(() => {
      this.refreshPromise = null;
    });
    return this.refreshPromise;
  }

  private async doRefresh(): Promise<StoredTokens> {
    const current = await this.options.store.getTokens();
    if (!current?.refreshToken) {
      throw new Error('No refresh token available');
    }
    const discovery = await this.discovery();
    try {
      const response = await refreshTokens({
        config: this.options.config,
        discovery,
        refreshToken: current.refreshToken,
        fetch: this.options.fetch,
      });
      const next = tokensToStored(response, current);
      await this.options.store.setTokens(next);
      this.emit({ type: 'tokens-refreshed' });
      return next;
    } catch (err) {
      // Refresh failed — typically expired refresh token. Log the user out.
      await this.options.store.clearTokens();
      this.emit({ type: 'logout' });
      throw err;
    }
  }

  private discovery(): Promise<OidcDiscovery> {
    if (!this.discoveryPromise) {
      this.discoveryPromise = discoverIssuer(this.options.config.issuer, {
        fetch: this.options.fetch,
      });
    }
    return this.discoveryPromise;
  }

  private isExpired(tokens: StoredTokens): boolean {
    if (!tokens.expiresAt) return false;
    const skewMs = (this.options.refreshSkewSeconds ?? 30) * 1000;
    return Date.now() + skewMs >= tokens.expiresAt;
  }

  private emit(event: AuthEvent): void {
    for (const l of this.listeners) {
      try {
        l(event);
      } catch {
        // never let a listener break the flow
      }
    }
  }
}

function isIdTokenExpiredError(err: unknown): boolean {
  return err instanceof Error && err.message === 'ID token expired';
}

function tokensToStored(tokens: TokenResponse, previous?: StoredTokens | null): StoredTokens {
  const expiresAt =
    tokens.expires_in !== undefined ? Date.now() + tokens.expires_in * 1000 : undefined;
  return {
    accessToken: tokens.access_token,
    // Keycloak rotates refresh tokens; fall back to the previous one if
    // the response didn't include a new one.
    refreshToken: tokens.refresh_token ?? previous?.refreshToken,
    idToken: tokens.id_token ?? previous?.idToken,
    expiresAt,
    scope: tokens.scope ?? previous?.scope,
  };
}
